import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '../../../utils/store';
import Constants from '../../../utils/constants';
import HttpService from '../../../services/HttpService';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AlertMessageComponent from '../../../components/AlertMessageComponent';
import { useAlert } from '../../../components/alertContext';
import HorizontalBar from './HorizontalBar';
import { format, addDays, isBefore } from 'date-fns';

import ButtonComponent from '../../../components/form/ButtonComponent';
import HttpServices from '../../../services/HttpService';

interface ProjectGanttChartProps {
  projectInternalId: number;
}
const ProjectGanttChart: React.FC<ProjectGanttChartProps> = ({
  projectInternalId,
}) => {
  const isInitialLoad = useRef(true);
  const { getAdmin } = useStore();

  const [shiftList, setShiftList] = useState<any>([]);
  const [chartData, setChartData] = useState<any>([]);
  const [chartDates, setChartDates] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [minChartDate, setMinChartDate] = useState<any>('');
  const [maxChartDate, setMaxChartDate] = useState<any>('');
  const { alert, setAlert, handleCloseAlert } = useAlert();

  const FetchShiftList = async () => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL}${Constants.projectDashboardsURL}/${projectInternalId}/shift`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        {
          limit: -1,
          offset: 0,
          isActive: 1,
        }
      );
      if (response.success) {
        setShiftList(response.data);
        const chrtData = getGanttChartData(response.data);
        setChartData(chrtData);
        if (chrtData.length > 0) {
          const dates = response.data.map((shift: any) => new Date(shift.date));
          const minDate: any = new Date(Math.min(...dates));
          const maxDate: any = new Date(Math.max(...dates));
          const chrtDates = getDateRangeWithMonths(minDate, maxDate);
          setMinChartDate(minDate.toISOString().split('T')[0]);
          setMaxChartDate(maxDate.toISOString().split('T')[0]);
          setChartDates(chrtDates);
          // console.log(JSON.stringify(chrtDates));
        }
        console.log(JSON.stringify(chrtData));
      }
      console.log('GET Response:', response);
    } catch (error: any) {
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  useEffect(() => {
    if (isInitialLoad.current && projectInternalId) {
      FetchShiftList();
    }
    isInitialLoad.current = false;
  }, [projectInternalId]);
  const sortResources = (chartResources: any) => {
    const finalResources = [];
    for (const shift of shiftList) {
      const { shiftBillables = [] } = shift;
      for (const billable of shiftBillables) {
        for (const resource of billable.resources || []) {
          const existResource = chartResources.find(
            (itemResource: any) =>
              itemResource.resourcesId === resource.resourcesId &&
              itemResource.shiftId === resource.shiftId
          );
          if (existResource) {
            finalResources.push({
              criticalPath: existResource.criticalPath,
              shiftResourceId: resource.shiftResourceId,
            });
          }
        }
      }
    }
    return finalResources;
  };
  const getGanttChartData = (shifts: any[]) => {
    if (!Array.isArray(shifts)) return [];

    const billableMap: any = new Map();

    for (const shift of shifts) {
      const shiftDate = new Date(shift.date);
      const { shiftBillables = [] } = shift;

      for (const billable of shiftBillables) {
        const billableKey = billable.billableItemId;

        if (!billableMap.has(billableKey)) {
          // Add new billable
          billableMap.set(billableKey, {
            ...billable,
            resources: [],
            startDate: shiftDate,
            endDate: shiftDate,
            days: 1,

            _resourceMap: new Map(),
          });
        } else {
          const existing = billableMap.get(billableKey);
          existing.startDate = new Date(
            Math.min(existing.startDate.getTime(), shiftDate.getTime())
          );
          existing.endDate = new Date(
            Math.max(existing.endDate.getTime(), shiftDate.getTime())
          );
          existing.days = existing.days + 1;
        }

        const billableEntry = billableMap.get(billableKey);

        for (const resource of billable.resources || []) {
          const resourceKey = resource.resourcesId;

          if (!billableEntry._resourceMap.has(resourceKey)) {
            billableEntry._resourceMap.set(resourceKey, {
              ...resource,
              startDate: shiftDate,
              endDate: shiftDate,

              days: 1,
              shiftDates: [shift.date],
            });
          } else {
            const existingResource =
              billableEntry._resourceMap.get(resourceKey);
            existingResource.startDate = new Date(
              Math.min(
                existingResource.startDate.getTime(),
                shiftDate.getTime()
              )
            );
            if (existingResource.shiftDates.indexOf(shift.date) === -1) {
              existingResource.shiftDates.push(shift.date);
            }

            existingResource.days = existingResource.days + 1;
            existingResource.endDate = new Date(
              Math.max(existingResource.endDate.getTime(), shiftDate.getTime())
            );
          }
        }
      }
    }

    // Convert to final output array
    const ganttData = [];

    for (const billable of billableMap.values()) {
      // Convert inner resourceMap to array and add formatted dates
      const resources: any = Array.from(billable._resourceMap.values()).map(
        (resource: any) => ({
          ...resource,
          start: resource.startDate.toISOString().split('T')[0],
          end: resource.endDate.toISOString().split('T')[0],
        })
      );

      ganttData.push({
        ...billable,
        resources,
        start: billable.startDate.toISOString().split('T')[0],
        end: billable.endDate.toISOString().split('T')[0],
      });
    }

    return ganttData;
  };

  const handleBillableDetailChange = (
    value: any,
    index: number,
    type: string,
    detailIndex: number
  ) => {
    setChartData((prevItems: any) => {
      // Create a copy of the current state
      const updatedItems = [...prevItems];
      // Update the specific item's property
      updatedItems[index]['resources'][detailIndex] = {
        ...updatedItems[index]['resources'][detailIndex],
        [type]: value,
      };

      return updatedItems;
    });
  };
  const getDayDifference = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const timeDiff = endDate.getTime() - startDate.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
  };
  const getLeftMargin = (
    firstStartDate: any,
    start: any,
    end: any,
    type: any
  ) => {
    var marginDifference = getDayDifference(firstStartDate, start) * 38;
    if (marginDifference === 38) {
      marginDifference = 18;
    } else {
      marginDifference = marginDifference - 38;
    }
    if (maxChartDate === start && type === 'inner') {
      marginDifference = marginDifference + 14;
    }
    if (maxChartDate === end && type === 'outer') {
      marginDifference = marginDifference + 14;
    }
    return marginDifference;
  };

  const getDateRangeWithMonths = (startStr: string, endStr: string): any => {
    const startDate = new Date(startStr);
    const endDate = new Date(endStr);
    const groups: any[] = [];
    let current = startDate;

    while (!isBefore(endDate, current)) {
      const iso = `${current.getDate()} ${current.toLocaleDateString('default', { weekday: 'short' }).charAt(0)}`;
      const monthLabel = format(current, 'MMM yyyy');
      const lastGroup: any = groups[groups.length - 1];

      if (!lastGroup || lastGroup.label !== monthLabel) {
        // start a new month group
        groups.push({ label: monthLabel, dates: [iso] });
      } else {
        // append to existing month group
        lastGroup.dates.push(iso);
      }

      current = addDays(current, 1);
    }

    return groups;
  };
  const saveCriticalPath = async () => {
    // const resourcesData = chartData.flatMap((entry: any) => entry.resources);

    const resourcesData = chartData
      .flatMap((entry: any) => entry.resources)
      .map((itemData: any) => ({
        resourcesId: itemData.resourcesId,
        criticalPath: itemData.criticalPath,
        shiftId: itemData.shiftId,
      }));

    const finalResponse = sortResources(resourcesData);

    try {
      setIsLoading(true);
      const endpoint = `${Constants.apiURL + Constants.shiftURL}/resources`;
      const response = await HttpServices.put(
        endpoint,
        { resources: finalResponse },
        {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        }
      );

      if (response.success) {
        setIsLoading(false);
        FetchShiftList();
      }
    } catch (error: any) {
      setIsLoading(false);
      console.error(`GET ERROR:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    } finally {
    }
  };

  return (
    <div className="containergrid">
      <div className="mt-2">
        <Paper elevation={0} sx={{ width: '100%' }} className="mt-4">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
              mb: 2,
            }}
          >
            <Typography variant="h6">Gantt Chart</Typography>
          </Box>
          <Grid container>
            <Grid size={6}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead
                    style={{
                      background: ' rgba(0, 244, 255, 0.06)',
                      height: '50px',
                    }}
                  >
                    <TableRow sx={{ height: '90px' }}>
                      <TableCell>Task name</TableCell>
                      <TableCell>Critical Path</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Start date</TableCell>
                      <TableCell>End date</TableCell>
                    </TableRow>
                  </TableHead>

                  {chartData.map((item: any, index: any) => {
                    return (
                      <TableBody key={'tbody_' + index}>
                        <TableRow key={'rowgnt_' + index}>
                          <TableCell
                            colSpan={2}
                            style={{ whiteSpace: 'pre-wrap' }}
                          >
                            {item.name}
                          </TableCell>
                          <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                            {item.days + ' Days'}
                          </TableCell>
                          <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                            {' '}
                            {item.start}
                          </TableCell>
                          <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                            {item.end}
                          </TableCell>
                        </TableRow>
                        {item['resources'].map(
                          (detailItem: any, detailIndex: any) => {
                            return (
                              detailItem && (
                                <TableRow
                                  key={
                                    'detail_row_' + index + '_' + detailIndex
                                  }
                                  sx={heightStyle}
                                >
                                  <TableCell
                                    sx={{
                                      paddingLeft: '50px',
                                      maxHeight: '100px',
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    {detailItem.name}
                                  </TableCell>
                                  <TableCell
                                    key={
                                      'bodyrow_dtl_cell_qty-' +
                                      index +
                                      '_' +
                                      detailIndex
                                    }
                                  >
                                    <TextField
                                      label=""
                                      key={'qtyFormBillable_' + index}
                                      name={'qtyFormBillable_' + index}
                                      value={detailItem.criticalPath}
                                      onChange={(event) => {
                                        handleBillableDetailChange(
                                          event.target.value,
                                          index,
                                          'criticalPath',
                                          detailIndex
                                        );
                                      }}
                                      type="number"
                                      style={{ width: '60px' }}
                                    />
                                  </TableCell>
                                  <TableCell
                                    key={
                                      'bodyrow_dtl_cell_cost-' +
                                      index +
                                      '_' +
                                      detailIndex
                                    }
                                    style={{ whiteSpace: 'pre-wrap' }}
                                  >
                                    {detailItem.days + ' Days'}
                                  </TableCell>
                                  <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                                    {' '}
                                    {detailItem.start}
                                  </TableCell>
                                  <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                                    {detailItem.end}
                                  </TableCell>
                                </TableRow>
                              )
                            );
                          }
                        )}
                      </TableBody>
                    );
                  })}
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={6}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead style={{ background: ' rgba(0, 244, 255, 0.06)' }}>
                    <TableRow sx={heightStyle}>
                      <TableCell>
                        <Box className={'monthRow'}>
                          {chartDates.map((dateItem: any) => {
                            return (
                              <Box sx={{ minWidth: '70px' }}>
                                <span className={'monthCell'}>
                                  {dateItem.label}
                                </span>
                                <Box className={'dayRow'}>
                                  {dateItem.dates.map((dates: any) => {
                                    return (
                                      <span className={'dayCell'}>{dates}</span>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  {chartData.map((item: any, index: any) => {
                    return (
                      <TableBody>
                        <TableRow sx={heightStyle}>
                          <TableCell sx={{ position: 'relative' }}>
                            <Box
                              className="resourcesBar"
                              sx={{
                                left:
                                  getLeftMargin(
                                    minChartDate,
                                    item.start,
                                    item.end,
                                    'outer'
                                  ) + 'px',
                              }}
                            >
                              <HorizontalBar
                                extraClass={
                                  item.start +
                                  '---' +
                                  item.end +
                                  '----' +
                                  maxChartDate
                                }
                                firstStartDate={chartData[0].start}
                                name="A"
                                start={item.start}
                                end={item.end}
                                color="#6947E0"
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                        {item['resources'].map(
                          (detailItem: any, detailIndex: any) => {
                            return (
                              detailItem && (
                                <TableRow sx={heightStyle}>
                                  <TableCell
                                    className="ABCBarHorz"
                                    style={{ position: 'relative' }}
                                  >
                                    {detailItem['shiftDates'].map(
                                      (childDate: any, childDateIndex: any) => {
                                        return (
                                          detailItem && (
                                            <Box
                                              className="resourcesBar"
                                              sx={{
                                                left:
                                                  getLeftMargin(
                                                    minChartDate,
                                                    childDate,
                                                    item.end,
                                                    'inner'
                                                  ) + 'px',
                                              }}
                                            >
                                              <HorizontalBar
                                                extraClass={
                                                  childDate +
                                                  '<--->' +
                                                  chartData[0].start +
                                                  '----' +
                                                  maxChartDate
                                                }
                                                firstStartDate={
                                                  chartData[0].start
                                                }
                                                name="Task A"
                                                start={childDate}
                                                end={childDate}
                                                color={
                                                  detailItem.criticalPath > 0
                                                    ? '#F3C352'
                                                    : '#BCC2D0'
                                                }
                                              />
                                            </Box>
                                          )
                                        );
                                      }
                                    )}
                                  </TableCell>
                                </TableRow>
                              )
                            );
                          }
                        )}
                      </TableBody>
                    );
                  })}
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={12} sx={{ textAlign: 'center', marginTop: '20px' }}>
              <ButtonComponent
                errors={[]}
                keyName="gntbtn"
                label={isLoading ? 'Saving...' : 'Save'}
                onClick={saveCriticalPath}
              />
            </Grid>
          </Grid>

          <AlertMessageComponent
            type={alert.type}
            open={alert.open}
            handleClose={handleCloseAlert}
            message={alert.message}
          />
        </Paper>
      </div>
    </div>
  );
};

export default ProjectGanttChart;

const heightStyle = {
  height: '90px',
  maxHeight: '90px',
};
